import { useCustomForm } from "@/hooks/useCustomForm"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  IconButton,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import ClearIcon from "@mui/icons-material/Clear"
import { z } from "zod"
import SingleSelect from "@/components/Select/SingleSelect"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import React, { useEffect, useState } from "react"
import { useCrudContext } from "@/context/crudContext"
import LetterAvatar from "@/components/Avatar/LetterAvatar"
import AddIcon from "@mui/icons-material/Add"
import AddContactModal from "../customers/Model"
import Grid from "@mui/material/Grid2"
import ProductableAdjustments from "@/components/Products/ProductableAdjustments"
import SecondaryButton from "@/components/Buttons/SecondaryButton"
import PrimaryButton from "@/components/Buttons/PrimaryButton"
import { DevTool } from "@hookform/devtools"
import { Textarea } from "@mantine/core"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { useNow } from "@mui/x-date-pickers/internals"
import dayjs from "dayjs"

const StockAdjustmentSchema = z.object({
  reference_no: z.string().nullable(),
  notes: z.string().nullable(),
  products: z
    .array(
      z.object({
        // Other fields...
      })
    )
    .min(1, "At least one item must be included"),
  date: z
    .any() // Accept any input (dayjs object or string)
    .transform((value) => {
      // Convert to Date object at end of day
      const dayjsObj = dayjs.isDayjs(value) ? value : dayjs(value, "YYYY-MM-DD")
      return dayjsObj.isValid() ? dayjsObj.endOf("day").toDate() : null
    })
    .refine((value) => value !== null, {
      message: "Invalid date format",
    }),
})

const AddStockAdjustmentModal = ({ open, onClose }) => {
  const queryClient = useQueryClient()
  const { openDialog, setOpenDialog, selectedRow, setSelectedRow } =
    useCrudContext()

  const {
    api,
    handleSubmit,
    errors,
    isSubmitting,
    reset,
    register,
    setValue,
    control,
    Controller,
    getValues,
  } = useCustomForm({
    resolver: zodResolver(StockAdjustmentSchema),
    defaultValues: {
      date: dayjs(),
    },
  })

  const defaultOnClose = () => {
    setOpenDialog(false)
    setSelectedRow(null)
  }

  useEffect(() => {
    if (selectedRow) {
      Object.entries(selectedRow).forEach(([key, value]) => {
        if (typeof value === "object" && value !== null) {
          Object.entries(value).forEach(([nestedKey, nestedValue]) => {
            setValue(`${key}.${nestedKey}`, nestedValue)
          })
        } else {
          setValue(`${key}`, value)
        }
      })
    } else {
      reset()
    }
  }, [selectedRow, setValue, reset])

  const onSubmit = async (data) => {
    const method = selectedRow ? "PUT" : "POST"
    const url = selectedRow
      ? `stock-adjustments/${selectedRow.id}`
      : "stock-adjustments"

    const response = await api(url, {
      method,
      body: JSON.stringify({ ...data, products: getValues("products") }),
    })

    if (response) {
      queryClient.invalidateQueries({ queryKey: ["stock-adjustments"] })
      toast.success(
        `Stock Adjustment successfully ${selectedRow ? "updated" : "added"}`
      )
      onClose ? onClose() : defaultOnClose()
      reset()
    }
  }

  return (
    <>
      <Modal
        open={open ? open : openDialog}
        onClose={defaultOnClose}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            margin: "100px auto",
            bgcolor: "white",
            width: "80vw",
            maxHeight: "90vh",
            overflowY: "auto",
            bRadius: 1,
            boxShadow: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bRadius: 1,
              background: "#f6f7f9",
              padding: { xs: "15px 20px", md: "25px" },
            }}
          >
            <Typography
              id="modal-modal-title"
              variant="h6"
              sx={{
                fontWeight: "600",
                fontSize: { xs: "16px", md: "18px" },
              }}
              className="text-black"
            >
              {selectedRow
                ? "Update Stock Adjustment"
                : "Add New Stock Adjustment"}
            </Typography>
            <IconButton
              aria-label="remove"
              size="small"
              onClick={onClose ? onClose : defaultOnClose}
            >
              <ClearIcon />
            </IconButton>
          </Box>

          <Box
            sx={{ padding: 2 }}
            component="form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 8 }}>
                <div>
                  <TextField
                    fullWidth
                    label="Reference No"
                    sx={{ marginBottom: 2 }}
                    error={!!errors.reference_no}
                    helperText={
                      errors.reference_no
                        ? errors.reference_no.message?.toString()
                        : ""
                    }
                    {...register("reference_no")}
                  />
                </div>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Controller
                    name="date"
                    control={control}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <DatePicker
                        label="Date"
                        value={value ? dayjs(value) : null}
                        onChange={(newValue) => onChange(newValue)} // <-- Don't format to string
                        sx={{
                          width: "100%",
                          "& fieldset": {
                            border: "1px solid #D5D9E2",
                            borderRadius: "7px",
                          },
                        }}
                        maxDate={dayjs()}
                        slotProps={{
                          textField: {
                            error: !!error,
                            helperText: error?.message,
                          },
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>

            {/* Productable Component */}
            <ProductableAdjustments
              label="Products"
              name="products"
              controller={Controller}
              control={control}
              errors={errors}
              defaultProducts={selectedRow?.products || []}
              helperText={errors.products?.message}
              onClose={onClose}
              isSubmitting={isSubmitting}
            />
            <Grid size={12}>
              <Textarea
                placeholder="Note"
                error={!!errors.notes}
                helperText={errors.notes?.message}
                {...register("notes")}
              />
            </Grid>
            <Grid size={12} mt={1}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "end",
                  gap: "10px",
                }}
              >
                <SecondaryButton label="Cancel" onClick={defaultOnClose} />
                <PrimaryButton
                  type="submit"
                  label="Create"
                  isSubmitting={isSubmitting}
                />
              </Box>
            </Grid>

            <DevTool control={control} />
          </Box>
        </Box>
      </Modal>
    </>
  )
}

export default AddStockAdjustmentModal
